/**
 * GFG-273 (Campaigns tab, GFG Admin) — frontend wire contract for the Campaigns tab: the table
 * row shape, the list/contacts endpoints, and the mutation responses (pause/resume/archive,
 * resend, remove). Field names mirror the backend twin, apps/gfgf-api/src/interfaces/adminCampaigns.ts,
 * symbol for symbol — keep the two in sync. UI strings for this tab live in
 * constants/adminCampaigns.ts, not here.
 */
import type { CSSProperties, ReactNode } from "react";
import type { FilterOption } from "@/components/BlotterFilterBar";
import type { AdminCampaignColumnId } from "@/constants/adminCampaigns";
// Re-exported from the single status source of truth (`constants/campaings.ts`, `CAMPAIGN_STATUSES`)
// so the admin table's vocabulary traces back to the one constant.
import type { AdminCampaignStatus } from "@/constants/campaings";

export type { AdminCampaignStatus };
export type AdminCampaignType = "Bulk" | "Automated";
/** Why a campaign's contacts are read-only; null = editable. */
export type AdminCampaignLock = "paused" | "ended" | null;
export type AdminCampaignSendMethod = "GfG" | "Manual";
export type AdminCampaignContactStatus =
  | "Delivered"
  | "Opened"
  | "Redeemed"
  | "Bounced"
  | "Pending";
export type AdminCampaignSortKey =
  | "name"
  | "client"
  | "account_lead"
  | "collection_value"
  | "status"
  | "recipients"
  | "scheduled_at"
  | "expiry"
  | "campaign_type"
  | "send_method"
  | "open_rate"
  | "redemption_rate"
  | "bounces"
  | "scheduled_reminders"
  | "thank_you_messages";

export { ADMIN_CAMPAIGN_STATUSES } from "@/constants/campaings";
export const ADMIN_CAMPAIGN_TYPES: readonly AdminCampaignType[] = ["Bulk", "Automated"];
export const ADMIN_CAMPAIGN_SEND_METHODS: readonly AdminCampaignSendMethod[] = ["GfG", "Manual"];
export const ADMIN_CAMPAIGN_SORT_KEYS: readonly AdminCampaignSortKey[] = [
  "name",
  "client",
  "account_lead",
  "collection_value",
  "status",
  "recipients",
  "scheduled_at",
  "expiry",
  "campaign_type",
  "send_method",
  "open_rate",
  "redemption_rate",
  "bounces",
  "scheduled_reminders",
  "thank_you_messages",
];

/** One row of the Campaigns table. */
export interface AdminCampaignRow {
  id: number;
  name: string;
  organizationId: number | null;
  /** The table's "Client" column. */
  organization: string;
  /** null → cell renders a dash; the Client popover reads "Unassigned". */
  accountLead: string | null;
  accountLeadEmail: string | null;
  /** Whole dollars; the cell formats it as "$125.00". */
  collectionValue: number;
  status: AdminCampaignStatus;
  recipients: number;
  /** ISO 8601 UTC, e.g. "2026-07-15T18:00:00Z". */
  scheduledAt: string;
  /** An Automated campaign never expires → null. */
  expiry: string | null;
  campaignType: AdminCampaignType;
  sendMethod: AdminCampaignSendMethod;
  /** Fraction 0..1; null while the campaign has not sent yet. */
  openRate: number | null;
  /** Fraction 0..1. */
  redemptionRate: number | null;
  bounces: number;
  scheduledReminders: number;
  thankYouMessages: number;
  kind: AdminCampaignKind;
  /** Which state a Paused campaign came from; drives "Paused (sched)" / "Paused (active)". */
  pausedFrom: "scheduled" | "active" | null;
  /** The ⋮ menu, computed by the server. */
  allowedActions: AdminCampaignAllowedActions;
  /** `allowed: false` keeps Impersonate in the menu, disabled, with `reason` as its tooltip. */
  impersonation: { allowed: boolean; reason: string | null };
}

export type AdminCampaignKind = "standard" | "automation" | "manual" | "universal";

/** `view` = the modal opens read-only; null = the action is not offered. */
export type AdminCampaignModalAccess = "edit" | "view" | null;

export interface AdminCampaignAllowedActions {
  impersonate: boolean;
  manageContacts: AdminCampaignModalAccess;
  manageGiftLinks: AdminCampaignModalAccess;
  pause: boolean;
  resume: boolean;
  cancel: boolean;
  disable: boolean;
  enable: boolean;
  restore: boolean;
  /** Offered but disabled when `allowed` is false; `reason` is the tooltip. */
  archive: { offered: boolean; allowed: boolean; reason: string | null };
}

/** Response of POST .../impersonate — the sign-in ticket plus the campaign to land on. */
export interface AdminCampaignImpersonationResult {
  success: true;
  data: {
    campaignId: number;
    organizationId: number | null;
    actorToken: { token: string; url: string; userId: string; actorId: string };
  };
}

/** Query params for `GET /admin/campaigns` — the api layer joins these into the querystring. */
export interface AdminCampaignsListParams {
  /** Trimmed, max 200 chars; matches campaign name OR client/org name. */
  search?: string;
  /** Org ids, as strings (csv on the wire). */
  organization_id?: string[];
  status?: AdminCampaignStatus[];
  campaign_type?: AdminCampaignType[];
  send_method?: AdminCampaignSendMethod[];
  /** `YYYY-MM-DD`; an inverted range (`from` after `to`) is a 400, not a silently empty result. */
  scheduled_from?: string;
  scheduled_to?: string;
  expiry_from?: string;
  expiry_to?: string;
  /** Must be one of ADMIN_CAMPAIGN_SORT_KEYS — an unknown value is a 400, not a dropped sort. */
  sortBy?: AdminCampaignSortKey;
  sortDir?: "asc" | "desc";
  page?: number;
  limit?: number;
  /** Asks this same request to also return `filterOptions.organizations`. */
  includeFilterOptions?: boolean;
}

export interface AdminCampaignsListResult {
  success: true;
  data: AdminCampaignRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  /** Present only when `includeFilterOptions` was requested; unaffected by active filters. */
  filterOptions?: { organizations: FilterOption[] };
}

/** Response of every status POST (pause, resume, cancel, archive, restore, disable, enable). */
export interface AdminCampaignStatusResult {
  success: true;
  data: { id: number; status: AdminCampaignStatus };
}

/**
 * The Campaigns table's column contract. Declared here rather than in campaignsColumns.tsx
 * because CampaignsTable and CampaignsPanel both consume it — an exported shape crossing module
 * boundaries belongs in interfaces/, not in the component file that happens to build it.
 */
export interface CampaignColumn {
  /** React key; for a configurable column it is also the id kept in the `cols` search param. */
  id: string;
  label: string;
  /** The key a header click sorts by, or null for Action — a menu button holds no value. */
  sortKey: AdminCampaignSortKey | null;
  /** Column width in px; the table sums these into its horizontal-scroll threshold. */
  width: number;
  /** Offset from the table's left edge when the column is frozen there. */
  pinnedLeft?: number;
  render: (row: AdminCampaignRow) => ReactNode;
  /** Merged over the shared cell style, never replacing it. */
  cellStyle?: CSSProperties;
  /** Merged over the shared header style, so a column's heading moves with its values. */
  headerStyle?: CSSProperties;
}

/** A column the user can hide or reorder — the only ones `useColumnPrefs` knows about. */
export interface ConfigurableCampaignColumn extends CampaignColumn {
  id: AdminCampaignColumnId;
}

/** Hook-derived handlers the fixed cells close over; the panel owns all of them. */
export interface CampaignsColumnDeps {
  /** The campaign name is a link: clicking it opens the Impersonate modal. */
  onOpenCampaign: (row: AdminCampaignRow) => void;
  /** The Client button opens the account-lead popover, anchored on the button clicked. */
  onOpenClient: (row: AdminCampaignRow, anchor: HTMLElement) => void;
  /** The three-dot button opens the row menu, anchored on itself. */
  onOpenMenu: (row: AdminCampaignRow, anchor: HTMLElement) => void;
  /** Row whose menu is open, so its trigger can report `aria-expanded`; null when none is. */
  openMenuRowId: number | null;
  /** Row whose client popover is open, for the same reason. */
  openClientRowId: number | null;
}
